import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiCreditsDto, AiReportResult, AiUsageDto, AskPilotReply, ReportSpec, SavedReportDto } from '@order-pilot/shared';
import { portalApi } from '@/lib/api';

const BASE = '/portal/ask-pilot';

export const askPilotKeys = {
  all: ['ask-pilot'] as const,
  credits: ['ask-pilot', 'credits'] as const,
  saved: ['ask-pilot', 'saved'] as const,
  usage: (month: string) => ['ask-pilot', 'usage', month] as const,
};

export function useAiCredits() {
  return useQuery({
    queryKey: askPilotKeys.credits,
    queryFn: ({ signal }) => portalApi.get<AiCreditsDto>(`${BASE}/credits`, { signal }),
    retry: false,
    staleTime: 60_000,
  });
}

export function useAiUsage(month: string) {
  return useQuery({
    queryKey: askPilotKeys.usage(month),
    queryFn: ({ signal }) => portalApi.get<AiUsageDto>(`${BASE}/usage?month=${encodeURIComponent(month)}`, { signal }),
  });
}

export function askQuestion(question: string): Promise<AskPilotReply> {
  return portalApi.post<AskPilotReply>(`${BASE}/ask`, { question });
}

export function useSavedReports() {
  return useQuery({
    queryKey: askPilotKeys.saved,
    queryFn: ({ signal }) => portalApi.get<SavedReportDto[]>(`${BASE}/saved`, { signal }),
    retry: false,
  });
}

export function useSaveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; question: string; spec: ReportSpec }) =>
      portalApi.post<SavedReportDto>(`${BASE}/saved`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: askPilotKeys.saved }),
  });
}

export function useRunSaved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => portalApi.get<AiReportResult>(`${BASE}/saved/${encodeURIComponent(id)}/run`),
    onSuccess: () => qc.invalidateQueries({ queryKey: askPilotKeys.saved }),
  });
}

export function useDeleteSaved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => portalApi.delete<void>(`${BASE}/saved/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: askPilotKeys.saved }),
  });
}
